import { useId, type HTMLAttributes, type ReactNode } from "react";
import { cx } from "@/lib/cx";

/**
 * The bento clip, verbatim from the source component. 5:2, objectBoundingBox
 * units. It notches out a top-left strip, a bottom-right block and a
 * bottom-left corner.
 *
 * The notches are FUNCTIONAL — the eyebrow and social tiles sit in the top
 * strip, the headline statistic in the bottom-right block. Never use the cut
 * without filling its notches. Once per page, hero only.
 */
const BENTO =
  "M0.0998072 1H0.422076H0.749756C0.767072 1 0.774207 0.961783 0.77561 0.942675V0.807325C0.777053 0.743631 0.791844 0.731953 0.799059 0.734076H0.969813C0.996268 0.730255 1.00088 0.693206 0.999875 0.675159V0.0700637C0.999875 0.0254777 0.985045 0.00477707 0.977629 0H0.902473C0.854975 0 0.890448 0.138535 0.850165 0.138535H0.0204424C0.00408849 0.142357 0 0.180467 0 0.199045V0.410828C0 0.449045 0.0136283 0.46603 0.0204424 0.469745H0.0523086C0.0696245 0.471019 0.0735527 0.497877 0.0733523 0.511146V0.915605C0.0723903 0.983121 0.090588 1 0.0998072 1Z";

export type ImagePlateProps = HTMLAttributes<HTMLElement> & {
  src: string;
  alt?: string;
  ratio?: string;
  caption?: ReactNode;
  index?: string;
  cut?: "none" | "bento";
  /** Alias for `cut="bento"`. */
  notch?: boolean;
  /** Absolutely positioned content layered over the plate — fills the notches. */
  overlay?: ReactNode;
};

function Caption({ index, caption }: { index?: string; caption?: ReactNode }) {
  if (!index && !caption) return null;
  return (
    <figcaption className="flex gap-4 items-baseline text-text-muted">
      {index && <span className="font-label text-label text-text-faint">{index}</span>}
      {caption && <span className="text-caption max-w-measure-narrow">{caption}</span>}
    </figcaption>
  );
}

export function ImagePlate({
  src,
  alt = "",
  ratio = "16/9",
  caption,
  index,
  cut = "none",
  notch = false,
  overlay = null,
  className,
  ...rest
}: ImagePlateProps) {
  const bento = cut === "bento" || notch;
  const clipId = `datum-bento-${useId().replace(/[^a-zA-Z0-9]/g, "")}`;

  return (
    <figure className={cx("m-0 flex flex-col gap-3", className)} {...rest}>
      {bento ? (
        <div className="relative">
          <svg viewBox="0 0 100 40" width="100%" height="100%" className="block w-full" role="img" aria-label={alt}>
            <defs>
              <clipPath id={clipId} clipPathUnits="objectBoundingBox">
                <path d={BENTO} />
              </clipPath>
            </defs>
            <image
              clipPath={`url(#${clipId})`}
              preserveAspectRatio="xMidYMid slice"
              width="100%"
              height="100%"
              href={src}
              className="plate-img"
            />
          </svg>
          {overlay}
        </div>
      ) : (
        <div className="relative overflow-hidden bg-surface-inset" style={{ aspectRatio: ratio }}>
          <img src={src} alt={alt} className="w-full h-full object-cover plate-img" />
          {overlay}
        </div>
      )}
      <Caption index={index} caption={caption} />
    </figure>
  );
}
